import React, { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import Badge from "@mui/material/Badge";
import headerLogo from "../../assets/images/Headerlogo_black.svg";
import {
  Box,
  Menu,
  MenuItem,
  CircularProgress,
  Typography,
  Divider,
  ListItemIcon,
  Avatar,
} from "@mui/material";
import { Logout } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  HEADER_HEIGHT,
  HEADER_BG,
  HEADER_BORDER,
  OFF_WHITE,
} from "../../constants/layout";
import authService from "../../redux/auth/authService";
import { getAdminHomeRoute } from "../../constants/adminAccess";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { resetState } from "../../redux/auth/authSlice";
import notificationService from "../../redux/notification/notificationService";
import api from "../../config/apiConfig";

const PROFILE_FONT = '"Inter", sans-serif';
const NAVY = "#051525";
const MUTED = "#4a6278";
const ACCENT = "#1E3A5F";
const PANEL_SHADOW = "0 8px 32px rgba(0, 0, 0, 0.1)";

const formatNotificationTime = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

const Header: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] =
    useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [unreadCount, setUnreadCount] = useState<number>(0); // State for unread notifications count
  const [countInNotification, setCountInNotification] = useState<number>(0); // State for unread notifications count
  const [newNotificationCount, setNewNotificationCount] = useState<number>(0);

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const userType = localStorage?.getItem("userType");

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Drawer removed

  const logout = () => {
    authService.logout();
    dispatch(resetState());
    navigate("/");
  };

  const markAllAsRead = async () => {
    await Promise.all(
      notifications.map((n) =>
        notificationService.readRequest({ requestId: n.id })
      )
    );
    setNotifications((prevNotifications) =>
      prevNotifications.map((n) => ({ ...n, isRead: true }))
    );
    setNewNotificationCount(0);
  };

  const handleNotificationClick = async (
    notificationId: string,
    notifyType: number,
    notifyTitle: string
  ) => {
    try {
      await notificationService.readRequest({ requestId: notificationId });

      setNotifications((prevNotifications) =>
        prevNotifications.map((n) =>
          n.id === notificationId ? { ...n, isRead: true } : n
        )
      );

      if (notifyType === 2) {
        navigate("/reports/fd/buy");
      } else if (notifyType === 3) {
        navigate("/requests");
        window.location.reload();
      } else if (notifyType === 4) {
        navigate("/reports/mf");
        window.location.reload();
      } else if (notifyType === 5) {
        navigate("/aif/buy");
        window.location.reload();
      } else if (notifyType === 6) {
        navigate("/pms/buy");
        window.location.reload();
      } else if (notifyType === 7) {
        navigate("/bonds");
        window.location.reload();
      } else if (notifyType === 8 && notifyTitle === "AIF Withdraw Request") {
        navigate("/aif/sell");
        window.location.reload();
      } else if (notifyType === 8 && notifyTitle === "PMS Withdraw Request") {
        navigate("/pms/sell");
        window.location.reload();
      } else if (
        notifyType === 13 ||
        (notifyType === 9 &&
          notifyTitle === "Interest In Private Stock Investment")
      ) {
        navigate("/private-stock/buy");
        window.location.reload();
      } else if (notifyType === 12) {
        navigate("/private-stock/sell");
        window.location.reload();
      } else if (
        notifyType === 9 &&
        notifyTitle === "Interest In Mutual Fund Investment"
      ) {
        navigate("/reports/mf");
        window.location.reload();
      } else if (
        notifyType === 9 &&
        notifyTitle === "Interest In Fixed Deposit Investment"
      ) {
        navigate("/reports/fd/buy");
        window.location.reload();
      } else if (
        notifyType === 9 &&
        notifyTitle === "Interest In AIF Investment"
      ) {
        navigate("/aif/buy");
        window.location.reload();
      } else if (
        notifyType === 9 &&
        notifyTitle === "Interest In PMS Investment"
      ) {
        navigate("/pms/buy");
        window.location.reload();
      } else if (notifyType === 10) {
        navigate("/reports/fd/sell");
        window.location.reload();
      }

      handleNotificationClose();
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
    setTimeout(() => {
      handleBellClick();
    }, 0); // slight delay to ensure the menu opens first
  };

  const handleBellClick = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error("Failed to mark notifications as read", error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    setRowsPerPage(50);
    try {
      const response = await notificationService.notification({
        notifyType: 0,
        pageIndex: page + 1,
        pageSize: rowsPerPage,
      });
      const notificationsData = response.data.data ?? [];

      const today = new Date();
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 2);

      const filteredNotifications = notificationsData.filter(
        (notification: any) => {
          const notificationDate = new Date(notification.createdAt);
          return notificationDate >= lastWeek && notificationDate <= today;
        }
      );

      const sortedNotifications = filteredNotifications.sort(
        (a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setNotifications(sortedNotifications);

      // Calculate unread count
      const countUnread = sortedNotifications.filter(
        (n: any) => !n.isRead
      ).length;
      setCountInNotification(countUnread);
      setNewNotificationCount(countUnread); // Set the new notification count

      setError(null);
    } catch (error) {
      setError("No notifications available");
      setNotifications([]);
      console.error("Failed to fetch notifications", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationService.requestCount({});
        console.log("Unread count data:", response); // Log response to verify the structure
        setUnreadCount(response.data); // Update this line to match the response structure
      } catch (error) {
        console.error("Failed to fetch unread notification count", error);
      }
    };

    fetchUnreadCount(); // Fetch count on component mount

    const interval = setInterval(() => {
      fetchUnreadCount(); // Fetch count every 3 minutes
    }, 180000); // 180000 milliseconds = 3 minutes

    return () => clearInterval(interval); // Clean up the interval on component unmount
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      setRowsPerPage(50);
      try {
        const response = await notificationService.notification({
          notifyType: 0,
          pageIndex: page + 1,
          pageSize: rowsPerPage,
        });
        const notificationsData = response.data.data ?? [];

        const today = new Date();
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 2);

        const filteredNotifications = notificationsData.filter(
          (notification: any) => {
            const notificationDate = new Date(notification.createdAt);
            return notificationDate >= lastWeek && notificationDate <= today;
          }
        );

        const sortedNotifications = filteredNotifications.sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setNotifications(sortedNotifications);

        // Calculate unread count
        const countUnread = sortedNotifications.filter(
          (n: any) => !n.isRead
        ).length;
        setCountInNotification(countUnread);

        setError(null);
      } catch (error) {
        setError("No notifications available");
        setNotifications([]);
        console.error("Failed to fetch notifications", error);
      } finally {
        setLoading(false);
      }
    };

    if (notificationsAnchorEl) {
      fetchNotifications();
    }
  }, [notificationsAnchorEl, page, rowsPerPage]);

  useEffect(() => {
    fetchNotifications(); // Fetch notifications on mount

    const interval = setInterval(() => {
      fetchNotifications(); // Fetch notifications every 3 minutes
    }, 180000); // 180000 milliseconds = 3 minutes

    return () => clearInterval(interval); // Clean up the interval on component unmount
  }, [page, rowsPerPage]); // Optional dependencies if page or rowsPerPage change

  const handleNotificationClose = () => {
    setNotificationsAnchorEl(null);
  };

  const fetchDetails = async () => {
    try {
      const accessToken = localStorage?.getItem("accessToken");
      const response = await api.post(
        "user/getById",
        {},
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      setUsername(response?.data?.data[0]?.aumUserId);
      setDisplayName(
        response?.data?.data[0]?.firstName ||
          localStorage.getItem("userName") ||
          ""
      );
      localStorage.setItem("AumUserID", response?.data?.data[0]?.aumUserId);
      localStorage.setItem("ID", response?.data?.data[0]?.id);
      localStorage.setItem("userName", response?.data?.data[0]?.firstName);
      // console.log("name", response?.data?.data[0]?.aumUserId);
    } catch (error) {
      console.error("Failed to fetch user Details", error);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, []);

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        height: HEADER_HEIGHT,
        width: "100%",
        backgroundColor: HEADER_BG,
        borderBottom: `1px solid ${HEADER_BORDER}`,
        color: "#000000",
      }}
    >
      <Toolbar
        sx={{
          height: HEADER_HEIGHT,
          minHeight: `${HEADER_HEIGHT}px !important`,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Link
          to={userType !== "4" ? "/dashboard" : getAdminHomeRoute(authService.getAumUserId())}
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            marginLeft: 12,
          }}
        >
          <img
            src={headerLogo}
            alt="AUM Sampann"
            style={{ height: 52, width: "auto", minWidth: 120, objectFit: "contain" }}
          />
        </Link>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {userType !== "4" && (
            <IconButton
              aria-label="notifications"
              onClick={handleClick}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "10px",
                width: 44,
                height: 44,
                color: "#000000",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              }}
            >
              <Badge
                badgeContent={newNotificationCount}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: 10,
                    minWidth: 18,
                    height: 18,
                  },
                }}
              >
                <NotificationsOutlinedIcon sx={{ fontSize: 22 }} />
              </Badge>
            </IconButton>
          )}

          <Menu
            id="notifications-menu"
            anchorEl={notificationsAnchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(notificationsAnchorEl)}
            onClose={handleNotificationClose}
            MenuListProps={{ sx: { p: 0 } }}
            PaperProps={{
              sx: {
                width: { xs: 320, sm: 400 },
                maxWidth: "calc(100vw - 32px)",
                maxHeight: 480,
                mt: 1.5,
                borderRadius: "16px",
                boxShadow: PANEL_SHADOW,
                border: `1px solid ${HEADER_BORDER}`,
                overflow: "hidden",
                fontFamily: PROFILE_FONT,
              },
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${HEADER_BORDER}`,
                bgcolor: "#ffffff",
              }}
            >
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 16, fontWeight: 700, color: NAVY }}>
                Notifications
              </Typography>
              {newNotificationCount > 0 ? (
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.25,
                    borderRadius: "999px",
                    bgcolor: OFF_WHITE,
                    fontSize: 12,
                    fontWeight: 600,
                    color: ACCENT,
                  }}
                >
                  {newNotificationCount} new
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                maxHeight: 380,
                overflowY: "auto",
                bgcolor: "#ffffff",
                "&::-webkit-scrollbar": { width: 5 },
                "&::-webkit-scrollbar-thumb": { bgcolor: "#D0D0D0", borderRadius: 4 },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                  <CircularProgress size={28} sx={{ color: ACCENT }} />
                </Box>
              ) : error ? (
                <Box sx={{ px: 2.5, py: 5, textAlign: "center" }}>
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: MUTED }}>
                    {error}
                  </Typography>
                </Box>
              ) : notifications && notifications.length > 0 ? (
                notifications.map((notification, index) => (
                  <Box
                    key={notification.id}
                    onClick={() =>
                      handleNotificationClick(
                        notification.id,
                        notification.notifyType,
                        notification.notifyTitle
                      )
                    }
                    sx={{
                      px: 2.5,
                      py: 1.75,
                      cursor: "pointer",
                      bgcolor: notification.isRead ? "#ffffff" : OFF_WHITE,
                      borderBottom:
                        index < notifications.length - 1 ? `1px solid ${HEADER_BORDER}` : "none",
                      transition: "background-color 0.15s ease",
                      "&:hover": { bgcolor: notification.isRead ? "#FAFAFA" : "rgba(241, 235, 235, 0.85)" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: notification.isRead ? "transparent" : ACCENT,
                          mt: 0.75,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: PROFILE_FONT,
                              fontSize: 14,
                              fontWeight: notification.isRead ? 500 : 700,
                              color: NAVY,
                              lineHeight: 1.35,
                            }}
                          >
                            {notification.notifyTitle}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: PROFILE_FONT,
                              fontSize: 11,
                              color: MUTED,
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                              mt: 0.25,
                            }}
                          >
                            {formatNotificationTime(notification.createdAt)}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: 13,
                            color: MUTED,
                            lineHeight: 1.45,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {notification.notifyMessage || "No message available"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box sx={{ px: 2.5, py: 5, textAlign: "center" }}>
                  <NotificationsOutlinedIcon sx={{ fontSize: 36, color: "#C5C5C5", mb: 1 }} />
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, fontWeight: 600, color: NAVY, mb: 0.5 }}>
                    No notifications
                  </Typography>
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: MUTED }}>
                    You&apos;re all caught up for now.
                  </Typography>
                </Box>
              )}
            </Box>
          </Menu>

          <Box
            onClick={handleMenu}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleMenu(e as unknown as React.MouseEvent<HTMLElement>);
            }}
            aria-label="account menu"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              px: 1,
              py: 0.5,
              borderRadius: "10px",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#000000",
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              {(displayName || username || "A").charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#000000",
                  lineHeight: 1.2,
                }}
              >
                {displayName || "Admin"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#888888", lineHeight: 1.2 }}>
                {username}
              </Typography>
            </Box>
            <KeyboardArrowDownIcon sx={{ color: "#666666", fontSize: 20 }} />
          </Box>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            keepMounted
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              elevation: 2,
              sx: {
                mt: 1,
                minWidth: 160,
                borderRadius: "10px",
                overflow: "visible",
              },
            }}
          >
            <Link to="/profile" style={{ textDecoration: "none", color: "inherit" }}>
              <MenuItem onClick={handleClose}>Profile</MenuItem>
            </Link>
            <Divider />
            <MenuItem
              onClick={() => {
                logout();
                handleClose();
              }}
            >
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
